<template>
    <div class="py-14 bg-gray-200">
        <form @submit.prevent="onSubmit" class="container max-w-4xl">
            <h2 class="text-center text-4xl font-bold font-heading">Find a course</h2>
            <div class="grid grid-cols-1 gap-y-8 gap-x-8 mt-12 md:grid-cols-3">
                <div class="md:col-span-3">
                    <h3 class="text-center text-xl font-light font-heading">
                        Search by keyword
                    </h3>
                    <Input
                        name="search"
                        class="mt-4"
                        v-model="search"
                        placeholder="Search..."
                        :clearable="true"/>
                </div>

                <div>
                    <h3 class="text-center text-xl font-light font-heading">
                        Find a course between
                    </h3>
                    <div class="flex flex-col gap-8 mt-4 sm:flex-row sm:gap-4">
                        <ElDatePicker
                            class="flex-1"
                            name="start"
                            format="DD/MM/YYYY"
                            value-format="YYYY-MM-DDTHH:mm:ssZ"
                            :disabledDate="disabledStartDate"
                            v-model="start"
                        />

                        <ElDatePicker
                            class="flex-1"
                            name="end"
                            format="DD/MM/YYYY"
                            value-format="YYYY-MM-DDTHH:mm:ss"
                            v-model="end"
                            :disabledDate="disabledEndDate"
                        />
                    </div>
                </div>
                <div>
                    <h3 class="text-center text-xl font-light font-heading">
                        Course pathway
                    </h3>
                    <Select
                        v-if="pathways.length"
                        name="pathway_id"
                        class="mt-4"
                        placeholder="Please select a pathway..."
                        :clearable="true"
                        :options="pathways"
                        v-model="pathway_id"/>
                </div>
                <div>
                    <h3 class="text-center text-xl font-light font-heading">
                        Course type
                    </h3>
                    <Select
                        v-if="course_types.length"
                        name="pathway_id"
                        class="mt-4"
                        placeholder="Please select a course type..."
                        :clearable="true"
                        :options="course_types"
                        v-model="course_type"/>
                </div>
            </div>
            <div class="mt-12 text-center">
                <Button v-if="!realtime" type="submit">
                    Search
                </Button>

                <div v-if="canClearFilters" class="mt-8">
                    <button @click.prevent="clearFilters">Clear filters</button>
                </div>
            </div>
        </form>
    </div>
</template>

<script>

import axios from "axios";
import dayjs from "dayjs";
import {optionsToArray} from "@/Lib/Helpers";
import {debounce, isNil, isString, omitBy, trim} from "lodash";
import Button from "@/Components/Button.vue"
import Input from "@/Components/Form/Input.vue"
import Select from "@/Components/Form/Select.vue"
import PaginationHelpers from "@/Mixins/PaginationHelpers";
import {ElDatePicker} from "element-plus"

export default {

    name: "CourseSearch",

    mixins: [
        PaginationHelpers
    ],

    props: {
        realtime: {
            type: Boolean,
            default: false
        },
        constraints: {
            type: Object,
            default: () => ({})
        },
    },

    data() {
        return {
            pathways: [],
            search: this.constraints.search || '',
            start: this.constraints.start || '',
            end: this.constraints.end || '',
            pathway_id: this.constraints.pathway_id || '',
            course_type: this.constraints.course_type || '',
            course_types: [
                {label:"All", value: ''},
                {label:"Educator Led", value: 1},
                {label:"E-Leraning", value: 2},
            ]
        }
    },

    async created() {
        await this.fetchPathways();
    },

    components: {
        Input,
        ElDatePicker,
        Select,
        Button
    },

    methods: {
        async fetchPathways() {
            this.pathways = optionsToArray((await axios.get(route('api.pathways.index') + '?list')).data);
        },
        getRouteData() {
            const name = 'courses';

            return this.$route.name === name ? this.$route : {name, query: {}};
        },
        updateStart(date) {
            this.start = date ? dayjs(date).startOf('day').toISOString() : '';
        },
        updateEnd(date) {
            this.end = date ? dayjs(date).endOf('day').toISOString() : '';
        },
        onSubmit() {
            if (!this.realtime) {
                this.$router.push(this.routeTo);
            }
        },
        disabledStartDate(date) {
            const afterEndDate = this.end ? dayjs(date).isAfter(dayjs(this.end).startOf('day')) : false;

            return afterEndDate || dayjs(date).isBefore(dayjs().startOf('day'));
        },
        disabledEndDate(date) {
            const beforeStartDate = this.start ? dayjs(date).isBefore(dayjs(this.start).startOf('day')) : false;

            return beforeStartDate || this.beforeToday();
        },
        beforeToday(date) {
            return dayjs(date).isBefore(dayjs().startOf('day'));
        },
        clearFilters() {
            this.search = null;
            this.start = null;
            this.end = null;
            this.pathway_id = null;
        },
    },

    computed: {
        filters() {
            return omitBy({
                search: this.search,
                start: this.start,
                end: this.end,
                pathway_id: this.pathway_id,
            }, value => isNil(value) || (isString(value) && trim(value) === ''));
        },
        canClearFilters() {
            return Object.keys(this.filters).length > 0;
        },
        routeTo() {
            return {
                name: 'courses',
                query: this.filters
            };
        }
    },

    watch: {
        search: debounce(function (search) {
            if (this.realtime) {
                this.getData(_.merge(this.constraints, {search, page: null}));
            }
        }, 200),
        start(start) {
            if (this.realtime) {
                this.getData(_.merge(this.constraints, {start, page: null}));
            }
        },
        end(end) {
            if (this.realtime) {
                this.getData(_.merge(this.constraints, {end, page: null}));
            }
        },
        pathway_id(pathway_id) {
            if (this.realtime) {
                this.getData(_.merge(this.constraints, {pathway_id, page: null}));
            }
        },
        course_type(course_type) {
            if (this.realtime) {
                this.getData(_.merge(this.constraints, {course_type, page: null}));
            }
        }
    }
};

</script>
